import React from 'react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { Badge } from '../components/ui/Badge';
import { Section, ErrorPanel, LoadingRow, EmptyNote, Bars, TableWrap, Th, Td, UserLink, MarketTag } from '../components/ui/bits';
import { DailySplit } from '../components/charts/DailySplit';
import { useRpc } from '../lib/queries';
import { agoLabel, fmtPct } from '../lib/format';
import { stageLabel } from '../lib/labels';
import type { OutcomesResp } from '../lib/types';

const both = (s: OutcomesResp['summary'], k: keyof OutcomesResp['summary']['uk']) => ({ uk: s.uk[k] ?? 0, us: s.us[k] ?? 0 });

/** Offers and rejections users log. Internal accounts excluded; UK/US from the user’s profile. */
export const OutcomesPage: React.FC = () => {
  const { data: o, error, isLoading } = useRpc<OutcomesResp>('admin_dash_outcomes');

  if (isLoading) return <LoadingRow label="Loading outcomes" />;
  if (error || !o) return <ErrorPanel error={error ?? 'No data returned'} label="admin_dash_outcomes" />;

  const s = o.summary;
  const offers = s.uk.offers + s.us.offers;
  const rej = s.uk.rejections + s.us.rejections;
  const withdrew = s.uk.withdrew + s.us.withdrew;
  const logged = offers + rej + withdrew;

  return (
    <div>
      <PageHeader
        title="Outcomes"
        subtitle="Offers and rejections our users log. The clearest proof the product works. Internal accounts are excluded."
      />

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3 mb-3">
        <StatTile label="Offers, all time" value={offers} split={both(s, 'offers')} sub={`${s.uk.users_with_offer + s.us.users_with_offer} people`} source="user_outcomes, outcome offer" />
        <StatTile label="Offers, 30 days" value={s.uk.offers_30d + s.us.offers_30d} split={both(s, 'offers_30d')} sub={`${s.uk.offers_7d + s.us.offers_7d} in 7 days`} />
        <StatTile label="Offers, last 365 days" value={s.uk.offers_365d + s.us.offers_365d} split={both(s, 'offers_365d')} />
        <StatTile label="Offer rate" value={fmtPct(offers, logged, 0)} sub={`Of ${logged} outcomes logged`} />
        <StatTile label="Rejections" value={rej} split={both(s, 'rejections')} sub={`${withdrew} withdrew`} />
        <StatTile label="People logging" value={s.uk.users_logging + s.us.users_logging} split={both(s, 'users_logging')} />
      </div>

      <Section title="Offers per day, 90 days" source="user_outcomes" className="mb-3">
        <DailySplit data={o.daily_offers_90d} unit="offers" height={160} />
      </Section>

      <div className="grid lg:grid-cols-2 gap-3 mb-3">
        <Section title="By firm" sub="Top 25 by offers." source="user_outcomes">
          {o.by_firm.length === 0 ? <EmptyNote>No outcomes logged</EmptyNote> : (
            <TableWrap>
              <thead><tr><Th>Firm</Th><Th className="text-right">Offers</Th><Th className="text-right">UK</Th><Th className="text-right">US</Th><Th className="text-right">Rejections</Th><Th className="text-right">Offer rate</Th></tr></thead>
              <tbody>
                {o.by_firm.map(r => (
                  <tr key={r.firm_name}>
                    <Td className="font-medium text-stone-900">{r.firm_name}</Td>
                    <Td className="text-right tabular-nums">{r.offers}</Td>
                    <Td className="text-right tabular-nums text-stone-500">{r.offers_uk}</Td>
                    <Td className="text-right tabular-nums text-stone-500">{r.offers_us}</Td>
                    <Td className="text-right tabular-nums">{r.rejections}</Td>
                    <Td className="text-right tabular-nums">{fmtPct(r.offers, r.offers + r.rejections, 0)}</Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Section>
        <div className="space-y-3">
          <Section title="Offers by university" source="user_outcomes joined to profiles">
            {o.by_university.length === 0 ? <EmptyNote>No offers logged</EmptyNote> : (
              <Bars rows={o.by_university.map(r => ({ key: r.university, label: r.university, n: r.offers }))} />
            )}
          </Section>
          <Section title="Offers by programme" source="user_outcomes.career_stage">
            {o.by_programme.length === 0 ? <EmptyNote>No offers logged</EmptyNote> : (
              <Bars rows={o.by_programme.map(r => ({ key: r.career_stage, label: stageLabel(r.career_stage), n: r.offers }))} />
            )}
          </Section>
          <Section title="Where rejections happen" source="user_outcomes.rejection_stage">
            {o.rejection_stages.length === 0 ? <EmptyNote>No rejections logged</EmptyNote> : (
              <Bars rows={o.rejection_stages.slice(0, 10).map(r => ({ key: r.stage, label: r.stage, n: r.n }))} />
            )}
          </Section>
          <Section title="Pay on offers" sub="Median of what users typed in. The app asks UK users for £ and US users for $ a year (the column is named compensation_gbp, but US rows hold dollars)." source="user_outcomes.compensation_gbp">
            <div className="grid grid-cols-2 gap-3 text-[13px]">
              <div><p className="text-stone-500">UK median</p><p className="text-[18px] font-semibold tabular-nums">{o.compensation.uk.median != null ? `£${Math.round(o.compensation.uk.median).toLocaleString('en-GB')}` : 'None'}</p><p className="text-[12px] text-stone-500">{o.compensation.uk.n} offers with pay</p></div>
              <div><p className="text-stone-500">US median</p><p className="text-[18px] font-semibold tabular-nums">{o.compensation.us.median != null ? `$${Math.round(o.compensation.us.median).toLocaleString('en-US')}` : 'None'}</p><p className="text-[12px] text-stone-500">{o.compensation.us.n} offers with pay</p></div>
            </div>
          </Section>
        </div>
      </div>

      <Section title="Latest outcomes" sub="Newest 50." source="user_outcomes">
        {o.recent.length === 0 ? <EmptyNote>No outcomes logged</EmptyNote> : (
          <TableWrap>
            <thead><tr><Th>Outcome</Th><Th>Firm</Th><Th>Programme</Th><Th>Stage</Th><Th>User</Th><Th>Market</Th><Th>When</Th></tr></thead>
            <tbody>
              {o.recent.map(r => (
                <tr key={r.id}>
                  <Td>
                    <Badge tone={r.outcome === 'offer' ? 'emerald' : r.outcome === 'rejection' ? 'rose' : 'stone'}>
                      {r.outcome === 'offer' ? 'Offer' : r.outcome === 'rejection' ? 'Rejected' : 'Withdrew'}
                    </Badge>
                    {r.shared_publicly && <Badge tone="indigo" className="ml-1">Shared</Badge>}
                  </Td>
                  <Td className="font-medium text-stone-900">{r.firm_name}</Td>
                  <Td>{stageLabel(r.career_stage)}</Td>
                  <Td>{r.rejection_stage ?? ''}</Td>
                  <Td><UserLink id={r.user_id} /></Td>
                  <Td><MarketTag market={r.market} /></Td>
                  <Td className="whitespace-nowrap">{agoLabel(r.created_at)}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        )}
      </Section>
    </div>
  );
};
